/**
 * Gets the authentication headers for API requests
 * @returns {Object} Headers object with Authorization if token exists
 */
export const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json'
  };

  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  return headers;
};

/**
 * Gets the authentication token from local storage
 * @returns {string|null} Authentication token or null if not found
 */
export const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  // Return token in proper format (with Bearer prefix)
  return token.startsWith('Bearer ') ? token.split(' ')[1] : token;
};

/**
 * Gets the valid tenant ID from local storage
 * @returns {string|null} Tenant ID or null if not found
 */
export const getValidTenantId = () => {
  // Try to get tenant ID from local storage
  const tenantId = localStorage.getItem('tenantId');
  if (tenantId) return tenantId;
  
  // If not found directly, try to extract from currentTenant object
  const currentTenant = localStorage.getItem('currentTenant');
  if (currentTenant) {
    try {
      const tenant = JSON.parse(currentTenant);
      if (tenant && (tenant._id || tenant.id)) {
        return tenant._id || tenant.id;
      }
    } catch (e) {
      console.error('Error parsing currentTenant:', e);
    }
  }
  
  return null;
};

/**
 * Check if the current user has a specific role
 * @param {String} role - The role to check for
 * @returns {Boolean} Whether the user has the role
 */
export const hasRole = (role) => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    
    const user = JSON.parse(userStr);
    if (!user) return false;
    
    if (typeof user.role === 'string') {
      return user.role.toLowerCase() === role.toLowerCase();
    }
    
    if (Array.isArray(user.roles)) {
      return user.roles.some(r => r.toLowerCase() === role.toLowerCase());
    }
    
    return false;
  } catch (err) {
    console.error('Error checking user role:', err);
    return false;
  }
};

/**
 * Helper function to extract user information from token
 * @returns {Object|null} User information or null if not available
 */
export const getUserFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    // Extract payload from JWT token (middle part between dots)
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(payload);
  } catch (err) {
    console.error('Error extracting user from token:', err);
    return null;
  }
};

/**
 * Utilities for authentication management
 */

/**
 * Ensures that the user authentication data is consistent across the application
 * by validating and fixing any discrepancies in localStorage
 * @returns {Object} Object containing user details and validation result
 */
export const ensureAuthConsistency = () => {
  try {
    // Check for token first
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No authentication token found');
      return { isAuthenticated: false };
    }
    
    // Check for user ID
    let userId = localStorage.getItem('userId');
    
    // If no userId, try to extract from authUser
    if (!userId) {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        try {
          const user = JSON.parse(authUser);
          if (user && (user._id || user.id)) {
            userId = user._id || user.id;
            // Store for future use
            localStorage.setItem('userId', userId);
            console.log('Recovered userId from authUser:', userId);
          }
        } catch (e) {
          console.error('Error parsing authUser:', e);
        }
      }
    }
    
    // Check for tenant ID
    let tenantId = localStorage.getItem('tenantId');
    
    // If no tenantId, try to extract from currentTenant
    if (!tenantId) {
      const currentTenant = localStorage.getItem('currentTenant');
      if (currentTenant) {
        try {
          const tenant = JSON.parse(currentTenant);
          if (tenant && (tenant._id || tenant.id)) {
            tenantId = tenant._id || tenant.id;
            // Store for future use
            localStorage.setItem('tenantId', tenantId);
            console.log('Recovered tenantId from currentTenant:', tenantId);
          }
        } catch (e) {
          console.error('Error parsing currentTenant:', e);
        }
      }
    }
    
    return {
      isAuthenticated: !!token,
      userId,
      tenantId,
      token: token ? `${token.substring(0, 10)}...` : null
    };
  } catch (error) {
    console.error('Error in ensureAuthConsistency:', error);
    return { isAuthenticated: false, error: error.message };
  }
};

/**
 * Validates an authentication token
 * @param {string} token - The token to validate
 * @returns {boolean} Whether the token appears valid
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    // Simple validation - check if token has three parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Check if token is expired by decoding payload
    const payload = JSON.parse(atob(parts[1]));
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    
    if (Date.now() >= expiryTime) {
      console.warn('Token has expired');
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('Error validating token:', e);
    return false;
  }
};

export default {
  ensureAuthConsistency,
  isTokenValid
};
